import {cloneElement, useContext} from 'react'

import PropTypes from 'prop-types'

import {ATOM_ICON_COLORS, ATOM_ICON_SIZES} from '@s-ui/react-atom-icon'
import Context from '@s-ui/react-context'
import IconClose from '@adv-mt/mt-svg-icons/lib/Close'
import IconHeartSolid from '@adv-mt/mt-svg-icons/lib/HeartSolid'
import {Button, BUTTON_INTENTS, BUTTON_SHAPES, BUTTON_SIZES, BUTTON_VARIANTS} from '@adv-mt/ui/button'
import {PLACEMENTS, Popover, POPOVER_ELEVATION, PopoverClose, PopoverContent, PopoverTrigger} from '@adv-mt/ui/popover'
import {Body1, Body2} from '@adv-mt/ui/typography'

const BASE_CLASS = 'mt-NavigationAdToolbar-favoriteLeadPopover'
// `bottom` centers on the trigger — on both detail toolbars the favorite button sits near the
// right edge of its row, so a centered popover grows past the content's right margin.
// `bottom-end` aligns the popover's right edge with the trigger's, growing it leftward instead.
const PLACEMENT = PLACEMENTS.BOTTOM_END

const FavoriteLeadPopover = ({children, isOpen, onClose, onConfirm}) => {
  const {i18n} = useContext(Context)

  const handleConfirm = () => {
    onConfirm()
    onClose()
  }

  return (
    <Popover open={isOpen} onOpenChange={isPopoverOpen => !isPopoverOpen && onClose()}>
      <PopoverTrigger render={({onClick, type, ...triggerProps}) => cloneElement(children, triggerProps)} />
      <PopoverContent
        aria-live="polite"
        className={BASE_CLASS}
        color="surface"
        elevation={POPOVER_ELEVATION.L}
        placement={PLACEMENT}
        role="status"
        showCaret={false}
      >
        <PopoverClose
          render={
            <Button
              aria-label={i18n.t('LEAD_POST_FAVORITE.POPOVER.CLOSE')}
              className={`${BASE_CLASS}-close`}
              shape={BUTTON_SHAPES.CIRCLE}
              size={BUTTON_SIZES.MEDIUM}
              variant={BUTTON_VARIANTS.FLAT}
            >
              <IconClose size={ATOM_ICON_SIZES.medium} />
            </Button>
          }
        />
        <div className={`${BASE_CLASS}-enrichment`}>
          <IconHeartSolid color={ATOM_ICON_COLORS.accent} size={ATOM_ICON_SIZES.medium} />
          <div className={`${BASE_CLASS}-text`}>
            <Body1 className={`${BASE_CLASS}-title`} highlight>
              {i18n.t('LEAD_POST_FAVORITE.POPOVER.TITLE')}
            </Body1>
            <Body2 className={`${BASE_CLASS}-body`}>{i18n.t('LEAD_POST_FAVORITE.POPOVER.BODY')}</Body2>
            <Button
              className={`${BASE_CLASS}-cta`}
              intent={BUTTON_INTENTS.PRIMARY}
              onClick={handleConfirm}
              shape={BUTTON_SHAPES.CIRCLE}
              size={BUTTON_SIZES.MEDIUM}
              variant={BUTTON_VARIANTS.SOLID}
            >
              {i18n.t('LEAD_POST_FAVORITE.POPOVER.CTA')}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

FavoriteLeadPopover.displayName = 'FavoriteLeadPopover'
FavoriteLeadPopover.propTypes = {
  /**
   * The trigger element the popover anchors to
   */
  children: PropTypes.node.isRequired,
  /**
   * Whether the popover is open
   */
  isOpen: PropTypes.bool,
  /**
   * Called on the X control, ESC and click-outside. Never sends a lead
   */
  onClose: PropTypes.func,
  /**
   * Called on the CTA. The popover also closes when this fires
   */
  onConfirm: PropTypes.func
}

FavoriteLeadPopover.defaultProps = {
  isOpen: false,
  onClose: () => {},
  onConfirm: () => {}
}

export default FavoriteLeadPopover
